import { useMemo, useState } from 'react'
import { api } from '../../api/client'
import { useQuery } from '../../api/hooks'
import type { RoleRow } from '../../api/types'
import { ErrorBanner, Field, Loading, Modal, Panel, Tag } from '../../components/ui'

/**
 * Roles are runtime-editable bundles of permissions (ARCHITECTURE §4), so "let Mona post entries
 * but not lock periods" is a checkbox rather than a deployment.
 */
export function RolesPage() {
  const roles = useQuery<RoleRow[]>('/admin/roles')
  const permissions = useQuery<string[]>('/admin/roles/permissions')
  const [editing, setEditing] = useState<RoleRow | null>(null)

  return (
    <>
      <ErrorBanner error={roles.error} />
      {roles.loading && <Loading />}

      {roles.data && (
        <Panel flush>
          <table>
            <thead>
              <tr>
                <th style={{ width: 160 }}>Role</th>
                <th>Description</th>
                <th style={{ width: 90 }} className="num">
                  Users
                </th>
                <th style={{ width: 110 }} className="num">
                  Permissions
                </th>
                <th style={{ width: 100 }} />
              </tr>
            </thead>
            <tbody>
              {roles.data.map((role) => (
                <tr key={role.id}>
                  <td className="strong">
                    {role.name} {role.isSystemRole && <Tag>built-in</Tag>}
                  </td>
                  <td className="soft">{role.description}</td>
                  <td className="num">{role.userCount}</td>
                  <td className="num">{role.permissions.length}</td>
                  <td>
                    <button className="subtle small" onClick={() => setEditing(role)}>
                      Permissions
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {editing && permissions.data && (
        <RolePermissions
          role={editing}
          allPermissions={permissions.data}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            roles.reload()
          }}
        />
      )}
    </>
  )
}

function RolePermissions({
  role,
  allPermissions,
  onClose,
  onSaved,
}: {
  role: RoleRow
  allPermissions: string[]
  onClose: () => void
  onSaved: () => void
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set(role.permissions))
  const [description, setDescription] = useState(role.description ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>()

  // Grouped by the part before the dot — accounts.*, journal.*, and so on.
  const groups = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const permission of allPermissions) {
      const area = permission.split('.')[0]
      map.set(area, [...(map.get(area) ?? []), permission])
    }
    return [...map.entries()]
  }, [allPermissions])

  function toggle(permission: string) {
    setSelected((previous) => {
      const next = new Set(previous)
      if (next.has(permission)) next.delete(permission)
      else next.add(permission)
      return next
    })
  }

  async function save() {
    setBusy(true)
    setError(undefined)

    try {
      await api(`/admin/roles/${role.id}`, {
        method: 'PUT',
        body: { name: role.name, description, permissions: [...selected] },
      })
      onSaved()
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title={`${role.name} — permissions`}
      width="wide"
      onClose={onClose}
      footer={
        <>
          <span className="muted" style={{ marginRight: 'auto', fontSize: 13 }}>
            {selected.size} of {allPermissions.length} granted
          </span>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} onDismiss={() => setError(undefined)} />

      <Field label="Description">
        <input value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>

      <div className="grid cols-3">
        {groups.map(([area, permissions]) => (
          <div key={area}>
            <label>{area}</label>
            {permissions.map((permission) => (
              <div className="checklist-item" key={permission}>
                <input
                  type="checkbox"
                  checked={selected.has(permission)}
                  onChange={() => toggle(permission)}
                />
                <div className="label">{permission.split('.').slice(1).join('.')}</div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </Modal>
  )
}
